import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { FeatureTableOptions } from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseString } from 'utils/parsingHelpers'
import {
  optionalStringSchema,
  positiveNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

import { GeckoPipeSupport } from './geckoPipeSupport'

export enum PipeIntegralSupportType {
  Anchor = 'anchor',
  CardelPad = 'cardel_pad',
  Dummy = 'dummy',
  Saddle = 'saddle',
  Shoe = 'shoe',
  Trunnion = 'trunnion',
  Unknown = 'unknown',
}

export interface GeckoPipeIntegralSupportJSON {
  name: string | undefined
  type: GeckoFeatureModelType.PipeIntegralSupport
  pipe_support_type: PipeIntegralSupportType | undefined
  feature_geometry: {
    x: number
    y: number
    height: number
    width: number
  }
}

export const NewGeckoPipeIntegralSupportJSON: GeckoPipeIntegralSupportJSON = {
  type: GeckoFeatureModelType.PipeIntegralSupport,
  name: 'New Pipe Integral Support',
  feature_geometry: {
    x: 0,
    y: 0,
    height: 0,
    width: 0,
  },
  pipe_support_type: undefined,
}

export class GeckoPipeIntegralSupport extends GeckoPipeSupport {
  public readonly type = GeckoFeatureModelType.PipeIntegralSupport
  public readonly height: GeckoMeasurement
  public readonly width: GeckoMeasurement

  private constructor(
    name: string | undefined,
    pipeSupportID: string,
    pipeSupportType: PipeIntegralSupportType | undefined,
    geometry: GeckoComponentCoords,
    height: GeckoMeasurement,
    width: GeckoMeasurement
  ) {
    super(name, pipeSupportID, pipeSupportType, geometry)
    this.height = height
    this.width = width
  }

  public static CreateFromJSON(
    componentID: string,
    pipeSupportID: string,
    json: GeckoPipeIntegralSupportJSON
  ): GeckoPipeIntegralSupport {
    const name = parseString(json.name)

    const pipeSupportType =
      json.pipe_support_type === undefined
        ? undefined
        : (json.pipe_support_type as PipeIntegralSupportType)

    const featureGeometryData = json.feature_geometry as Record<string, number>
    const geometry = GeckoComponentCoords.CreateFromValues(
      componentID,
      featureGeometryData.x,
      GeckoMeasurementUnit.Meters,
      featureGeometryData.y,
      GeckoMeasurementUnit.Meters
    )

    const height = GeckoMeasurement.CreateFromValue(
      featureGeometryData.height as number,
      GeckoMeasurementUnit.Meters
    )
    const width = GeckoMeasurement.CreateFromValue(
      featureGeometryData.width as number,
      GeckoMeasurementUnit.Meters
    )

    return new GeckoPipeIntegralSupport(
      name,
      pipeSupportID,
      pipeSupportType,
      geometry,
      height,
      width
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoPipeIntegralSupportJSON>
  ): Map<string, GeckoPipeIntegralSupport> {
    return Object.keys(recordsJSON).reduce((resultObj, id) => {
      resultObj.set(
        id,
        GeckoPipeIntegralSupport.CreateFromJSON(
          componentID,
          id,
          recordsJSON[id]
        )
      )
      return resultObj
    }, new Map<string, GeckoPipeIntegralSupport>())
  }

  public asJSON() {
    return generatePipeIntegralSupport(
      this.name,
      this.geometry.x,
      this.geometry.y,
      this.height,
      this.width,
      this.pipeSupportType as PipeIntegralSupportType
    )
  }
}

export function generatePipeIntegralSupport(
  name: string | undefined,
  x: GeckoMeasurement,
  y: GeckoMeasurement,
  height: GeckoMeasurement,
  width: GeckoMeasurement,
  pipeSupportType: PipeIntegralSupportType | undefined
): GeckoPipeIntegralSupportJSON {
  return {
    name: name,
    type: GeckoFeatureModelType.PipeIntegralSupport,
    feature_geometry: {
      x: x
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      y: y
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      height: height
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      width: width
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
    },
    pipe_support_type: pipeSupportType,
  }
}

export const PipeIntegralSupportFormFields = (
  json: GeckoPipeIntegralSupportJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Name',
      value: json.name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: json.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: json.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'height',
      label: 'Height',
      value: json.feature_geometry.height,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: positiveNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'width',
      label: 'Width',
      value: json.feature_geometry.width,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: positiveNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'pipe_support_type',
      label: 'Pipe Support Type',
      value: json.pipe_support_type,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      options: [
        {
          value: PipeIntegralSupportType.Unknown,
          label: 'Unknown',
        },
        {
          value: PipeIntegralSupportType.Shoe,
          label: 'Shoe',
        },
        {
          value: PipeIntegralSupportType.Saddle,
          label: 'Saddle',
        },
        {
          value: PipeIntegralSupportType.Anchor,
          label: 'Anchor',
        },
        {
          value: PipeIntegralSupportType.CardelPad,
          label: 'Cardel Pad',
        },
        {
          value: PipeIntegralSupportType.Trunnion,
          label: 'Trunnion',
        },
        {
          value: PipeIntegralSupportType.Dummy,
          label: 'Dummy',
        },
      ],
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
  ]
}

export const PipeIntegralSupportJSONFromFormFields = (
  formFields: FormField[]
) => {
  return generatePipeIntegralSupport(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'height')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'width')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    getFormFieldByDataKey(formFields, 'pipe_support_type')
      ?.value as PipeIntegralSupportType
  )
}

export const PipeIntegralSupportFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.PipeIntegralSupport,
  title: 'Pipe Integral Supports',
  singularName: 'Pipe Integral Support',
  fields: ['x', 'y', 'height', 'width', 'pipe_support_type'],
}
